// Two timestamp shapes arrive on the wire.
//
// Weco's own is "2025-02-27 10:57:13.785317 UTC" -- space separated,
// microsecond precision, explicit "UTC" suffix. That is not ISO-8601, so it
// must not be handed to `new Date()` (whose parsing of this exact shape is
// undefined/engine-dependent); it is matched explicitly instead.
//
// Denshi's topics are republished by mqtt-to-bigquery, whose
// `_wall_clock_iso()` stamps them with Python's `datetime.isoformat()`:
// "2026-09-15T10:57:13.785317+00:00". Always UTC in practice, but the offset
// is honoured anyway rather than assumed.
const WECO_TIMESTAMP_RE = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?\s*UTC$/;
const ISO_TIMESTAMP_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(?:Z|([+-])(\d{2}):?(\d{2}))?$/;

/**
 * @param {string} raw
 * @returns {Date | null}
 */
export function parseWecoTimestamp(raw) {
  if (typeof raw !== 'string') return null;

  const text = raw.trim();
  let match = WECO_TIMESTAMP_RE.exec(text);
  let offsetMinutes = 0;

  if (!match) {
    match = ISO_TIMESTAMP_RE.exec(text);
    if (!match) return null;

    const [, , , , , , , , sign, offH, offM] = match;
    // No suffix at all is read as UTC: that is what every producer here means.
    if (sign) offsetMinutes = (sign === '-' ? -1 : 1) * (Number(offH) * 60 + Number(offM));
  }

  const [, year, month, day, hour, minute, second, fraction] = match;
  const ms = fraction ? Math.round(Number(`0.${fraction}`) * 1000) : 0;

  const utcMs = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    ms
  );

  return new Date(utcMs - offsetMinutes * 60_000);
}

// Camera files are named camera_<id>-<Y>_<m>_<d>-<H>_<M>_<S>.<ext>, e.g.
// "camera_1-2026_09_13-11_10_54.jpg". The producer stamps these in UTC
// (datetime.now(timezone.utc) in wec-pi-client), so they are read as UTC.
// Any bucket path prefix is tolerated -- only the basename is matched.
const CAMERA_FILENAME_RE =
  /camera_(\d+)-(\d{4})_(\d{2})_(\d{2})-(\d{2})_(\d{2})_(\d{2})\.(\w+)$/;

/**
 * @param {string} filename
 * @returns {{cameraId: number, takenAt: Date, isVideo: boolean} | null}
 */
export function parseCameraFilename(filename) {
  if (typeof filename !== 'string') return null;

  const match = CAMERA_FILENAME_RE.exec(filename.trim());
  if (!match) return null;

  const [, cameraId, year, month, day, hour, minute, second, ext] = match;

  return {
    cameraId: Number(cameraId),
    takenAt: new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      )
    ),
    isVideo: ext.toLowerCase() === 'mp4',
  };
}
